import { createApi } from "@reduxjs/toolkit/query/react";
import { CollectionData, PaginationResults, TypeQuery } from "@/lib/type";
import { baseQueryWithLogout } from "@/lib/baseQuery";
import {
  MenuCombo,
  MenuComboComponent,
  MenuComboFormData,
  MenuComboComponentFormData,
} from "./menu_combo.type";

export const MenuComboApi = createApi({
  reducerPath: "menuComboApi",
  tagTypes: ["menuCombo", "menuCombos", "menuComboComponent"],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    getMenuCombos: build.query<
      PaginationResults<MenuCombo>,
      { menuId: string; query?: TypeQuery }
    >({
      query: ({ menuId, query }) => ({
        url: `menus/${menuId}/menu_combos`,
        method: "GET",
        params: query,
      }),
      providesTags: ["menuCombos"],
      transformResponse: (data: CollectionData<MenuCombo>) => {
        return {
          results: data["hydra:member"],
          count: data["hydra:totalItems"],
        };
      },
    }),

    getMenuCombo: build.query<MenuCombo, string>({
      query: (id) => `menu_combos/${id}`,
      providesTags: ["menuCombo"],
    }),

    createMenuCombo: build.mutation<MenuCombo, MenuComboFormData>({
      query: (data) => ({
        url: "menu_combos",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["menuCombos", "menuCombo"],
    }),

    updateMenuCombo: build.mutation<
      MenuCombo,
      { id: string; data: Partial<MenuComboFormData> }
    >({
      query: ({ id, data }) => ({
        url: `menu_combos/${id}`,
        method: "PATCH",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
      }),
      invalidatesTags: ["menuCombos", "menuCombo"],
    }),

    deleteMenuCombo: build.mutation<void, string>({
      query: (id) => ({
        url: `menu_combos/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["menuCombos"],
    }),

    getMenuComboComponents: build.query<
      PaginationResults<MenuComboComponent>,
      { menuComboId: string; query?: TypeQuery }
    >({
      query: ({ menuComboId, query }) => ({
        url: `menu_combos/${menuComboId}/menu_combo_components`,
        method: "GET",
        params: query,
      }),
      providesTags: ["menuComboComponent"],
      transformResponse: (data: CollectionData<MenuComboComponent>) => {
        return {
          results: data["hydra:member"],
          count: data["hydra:totalItems"],
        };
      },
    }),

    createMenuComboComponent: build.mutation<
      MenuComboComponent,
      MenuComboComponentFormData
    >({
      query: (data) => ({
        url: "menu_combo_components",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["menuComboComponent", "menuCombo"],
    }),

    updateMenuComboComponent: build.mutation<
      MenuComboComponent,
      { id: string; data: Partial<MenuComboComponentFormData> }
    >({
      query: ({ id, data }) => ({
        url: `menu_combo_components/${id}`,
        method: "PATCH",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
      }),
      invalidatesTags: ["menuComboComponent", "menuCombo"],
    }),

    deleteMenuComboComponent: build.mutation<void, string>({
      query: (id) => ({
        url: `menu_combo_components/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["menuComboComponent", "menuCombo"],
    }),
  }),
});

export const {
  useGetMenuCombosQuery,
  useGetMenuComboQuery,
  useCreateMenuComboMutation,
  useUpdateMenuComboMutation,
  useDeleteMenuComboMutation,
  useGetMenuComboComponentsQuery,
  useCreateMenuComboComponentMutation,
  useUpdateMenuComboComponentMutation,
  useDeleteMenuComboComponentMutation,
} = MenuComboApi;
